"use client";

import React, { useEffect, useState } from "react";

const STAGGER_BASE_MS = 200;
const STAGGER_STEP_MS = 200;
const SENTENCE_PAUSE_MS = 600;
const CLAUSE_PAUSE_MS = 320;

const SENTENCE_PUNCT = /[.!?。！？؟]$/u;
const CLAUSE_PUNCT = /[,;:，；：、،؛]$/u;
const PURE_PUNCT = /^[.,;:!?。！？؟，；：、،؛]+$/u;

function getTrailingPause(token: string): number {
    if (SENTENCE_PUNCT.test(token)) return SENTENCE_PAUSE_MS;
    if (CLAUSE_PUNCT.test(token)) return CLAUSE_PAUSE_MS;
    return 0;
}

function countWords(node: React.ReactNode): number {
    if (typeof node === 'string') return (node.match(/\S+/g) || []).length;
    if (Array.isArray(node)) return node.reduce<number>((sum, n) => sum + countWords(n), 0);
    if (React.isValidElement(node)) {
        return countWords((node.props as { children?: React.ReactNode }).children);
    }
    return 0;
}

function withStaggeredWords(children: React.ReactNode): React.ReactNode {
    let cumulative = STAGGER_BASE_MS;
    let prevDelay = STAGGER_BASE_MS;

    const wrap = (key: string, content: React.ReactNode, delay: number, kind: 'word' | 'accent' = 'word') => {
        const animClass = kind === 'accent'
            ? 'inline-block motion-safe:[animation:accent-pulse_1100ms_cubic-bezier(0.2,0.8,0.2,1)_both]'
            : 'inline-block motion-safe:[animation:word-rise_500ms_ease-out_both]';
        return (
            <span
                key={key}
                className={animClass}
                style={{ animationDelay: `${delay}ms` }}
            >
                {content}
            </span>
        );
    };

    const process = (node: React.ReactNode, kp: string): React.ReactNode => {
        if (node == null || typeof node === 'boolean') return null;

        if (typeof node === 'string' || typeof node === 'number') {
            const tokens = String(node).split(/(\s+)/);
            return tokens.map((tok, i) => {
                if (tok === '') return null;
                if (/^\s+$/.test(tok)) return tok;

                const isPurePunct = PURE_PUNCT.test(tok);
                if (isPurePunct) {
                    const delay = prevDelay;
                    cumulative = Math.max(cumulative, prevDelay + 50) + getTrailingPause(tok);
                    return wrap(`${kp}-w${i}`, tok, delay);
                }

                const delay = cumulative;
                cumulative += STAGGER_STEP_MS + getTrailingPause(tok);
                prevDelay = delay;
                return wrap(`${kp}-w${i}`, tok, delay);
            });
        }

        if (Array.isArray(node)) return node.map((n, i) => process(n, `${kp}-${i}`));

        if (React.isValidElement(node)) {
            const innerWords = Math.max(1, countWords(node));
            const delay = cumulative;
            cumulative += innerWords * STAGGER_STEP_MS;
            prevDelay = delay;
            return wrap(`${kp}-el`, node, delay, 'accent');
        }

        return node;
    };

    return process(children, 'sw');
}

interface Props {
    className?: string;
    children: React.ReactNode;
}

export function StaggeredSubtitle({ className, children }: Props) {
    const [animated, setAnimated] = useState(false);

    useEffect(() => {
        const handle = requestAnimationFrame(() => setAnimated(true));
        return () => cancelAnimationFrame(handle);
    }, []);

    return (
        <p className={className}>
            {animated ? withStaggeredWords(children) : children}
        </p>
    );
}
